// Self-contained "Coworker self-enrollment" panel embedded in
// CompanyFormPage's edit view. Mirrors the section-on-form idioms (loading/
// error/empty/data, LayerCard + Table, toast on mutation, ConfirmDialog for
// destructive/irreversible actions) established by
// src/components/access-readers/access-readers-section.tsx and
// src/components/access-spans/access-spans-editor.tsx — see those for the
// pattern this follows (Access Provider Phase 3, Task 9 on the admin side;
// API client is src/api/enrollment.ts, Task 6/7/8 on the API side).
import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useState } from 'react'
import {
  Banner,
  Button,
  ClipboardText,
  Empty,
  Label,
  LayerCard,
  Loader,
  Switch,
  Table,
  Text,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { CheckIcon, ProhibitIcon, UserPlusIcon } from '@phosphor-icons/react'
import { ApiError } from '../../api/client'
import {
  approveEnrollment,
  getCompanyEnrollment,
  listPendingEnrollments,
  regenerateEnrollmentToken,
  rejectEnrollment,
  setEnrollmentApproval,
  type CompanyEnrollmentConfig,
  type CoworkerEnrollment,
} from '../../api/enrollment'
import { useTranslation } from '../../i18n'
import { ConfirmDialog } from '../ConfirmDialog'

/** Renders a pending enrollment's tax id, falling back to an em dash when unset. */
function formatPendingTaxId(pending: CoworkerEnrollment): string {
  return pending.taxIdValue ?? '—'
}

export function CompanyEnrollmentPanel({ companyId }: { companyId: string }) {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()

  const [config, setConfig] = useState<CompanyEnrollmentConfig | null>(null)
  const [pending, setPending] = useState<CoworkerEnrollment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [regenerateOpen, setRegenerateOpen] = useState(false)
  const [regenerating, setRegenerating] = useState(false)

  const [approvalToggling, setApprovalToggling] = useState(false)

  const [approvingId, setApprovingId] = useState<string | null>(null)
  const [rejectTarget, setRejectTarget] = useState<CoworkerEnrollment | null>(null)
  const [rejecting, setRejecting] = useState(false)

  const load = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      const [nextConfig, nextPending] = await Promise.all([
        getCompanyEnrollment(companyId, token),
        listPendingEnrollments(companyId, token),
      ])
      setConfig(nextConfig)
      setPending(nextPending)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [companyId, getAccessTokenSilently, t])

  useEffect(() => {
    void load()
  }, [load])

  function reportError(err: unknown) {
    toasts.add({
      variant: 'error',
      title: t('common.errorTitle'),
      description: err instanceof ApiError ? err.message : t('common.error'),
    })
  }

  async function handleRegenerate() {
    setRegenerating(true)
    try {
      const token = await getAccessTokenSilently()
      const result = await regenerateEnrollmentToken(companyId, token)
      setConfig((prev) =>
        prev
          ? { ...prev, enrollmentToken: result.enrollmentToken, inviteUrl: result.inviteUrl }
          : prev,
      )
      setRegenerateOpen(false)
      toasts.add({ variant: 'success', title: t('enrollment.regenerateSuccess') })
    } catch (err) {
      reportError(err)
    } finally {
      setRegenerating(false)
    }
  }

  async function handleApprovalToggle(next: boolean) {
    setApprovalToggling(true)
    try {
      const token = await getAccessTokenSilently()
      await setEnrollmentApproval(companyId, next, token)
      setConfig((prev) => (prev ? { ...prev, enrollmentApprovalRequired: next } : prev))
      toasts.add({ variant: 'success', title: t('enrollment.approvalRequired.updateSuccess') })
    } catch (err) {
      reportError(err)
    } finally {
      setApprovalToggling(false)
    }
  }

  async function handleApprove(enrollment: CoworkerEnrollment) {
    setApprovingId(enrollment.id)
    try {
      const token = await getAccessTokenSilently()
      await approveEnrollment(companyId, enrollment.id, token)
      setPending((prev) => prev.filter((p) => p.id !== enrollment.id))
      toasts.add({ variant: 'success', title: t('enrollment.approveSuccess') })
    } catch (err) {
      reportError(err)
    } finally {
      setApprovingId(null)
    }
  }

  async function handleReject(enrollment: CoworkerEnrollment) {
    setRejecting(true)
    try {
      const token = await getAccessTokenSilently()
      await rejectEnrollment(companyId, enrollment.id, token)
      setPending((prev) => prev.filter((p) => p.id !== enrollment.id))
      setRejectTarget(null)
      toasts.add({ variant: 'success', title: t('enrollment.rejectSuccess') })
    } catch (err) {
      reportError(err)
    } finally {
      setRejecting(false)
    }
  }

  return (
    <LayerCard>
      <LayerCard.Primary>
        <div className="flex flex-col gap-4">
          <Text as="h2" variant="heading2">
            {t('enrollment.title')}
          </Text>

          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader size="lg" />
            </div>
          )}

          {!loading && error && (
            <Banner variant="error">
              {t('common.errorPrefix')} {error}
            </Banner>
          )}

          {!loading && !error && config && (
            <>
              <div className="flex flex-col gap-1">
                <Label>{t('enrollment.inviteLink.label')}</Label>
                <div className="flex gap-2">
                  <ClipboardText
                    text={config.inviteUrl ?? ''}
                    labels={{ copyAction: t('enrollment.inviteLink.copy') }}
                    tooltip={{ copiedText: t('enrollment.inviteLink.copySuccess') }}
                  />
                  <Button variant="secondary" size="sm" onClick={() => setRegenerateOpen(true)}>
                    {t('enrollment.regenerate')}
                  </Button>
                </div>
              </div>

              <Switch
                label={t('enrollment.approvalRequired.label')}
                checked={config.enrollmentApprovalRequired}
                disabled={approvalToggling}
                onCheckedChange={(next: boolean) => void handleApprovalToggle(next)}
              />

              <div className="flex flex-col gap-2">
                <Text as="h3" variant="heading3">
                  {t('enrollment.pending.title')}
                </Text>

                {pending.length === 0 && (
                  <Empty
                    icon={<UserPlusIcon size={48} className="text-kumo-inactive" />}
                    title={t('enrollment.pending.empty')}
                  />
                )}

                {pending.length > 0 && (
                  <div style={{ overflowX: 'auto', width: '100%' }}>
                    <Table>
                      <Table.Header>
                        <Table.Row>
                          <Table.Head>{t('enrollment.pending.name')}</Table.Head>
                          <Table.Head>{t('enrollment.pending.email')}</Table.Head>
                          <Table.Head>{t('enrollment.pending.taxId')}</Table.Head>
                          <Table.Head>{t('enrollment.pending.actions')}</Table.Head>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {pending.map((item) => (
                          <Table.Row
                            key={item.id}
                            data-testid={`pending-enrollment-row-${item.id}`}
                          >
                            <Table.Cell>{item.name}</Table.Cell>
                            <Table.Cell>{item.email}</Table.Cell>
                            <Table.Cell>{formatPendingTaxId(item)}</Table.Cell>
                            <Table.Cell>
                              <div className="flex gap-2">
                                <Button
                                  variant="primary"
                                  size="sm"
                                  icon={<CheckIcon size={16} />}
                                  loading={approvingId === item.id}
                                  onClick={() => void handleApprove(item)}
                                >
                                  {t('enrollment.approve')}
                                </Button>
                                <Button
                                  variant="secondary-destructive"
                                  size="sm"
                                  icon={<ProhibitIcon size={16} />}
                                  onClick={() => setRejectTarget(item)}
                                >
                                  {t('enrollment.reject')}
                                </Button>
                              </div>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </LayerCard.Primary>

      <ConfirmDialog
        open={regenerateOpen}
        onOpenChange={setRegenerateOpen}
        title={t('enrollment.regenerateConfirm.title')}
        description={t('enrollment.regenerateConfirm.description')}
        confirmLabel={t('enrollment.regenerateConfirm.confirm')}
        variant="destructive"
        loading={regenerating}
        onConfirm={() => handleRegenerate()}
      />

      <ConfirmDialog
        open={Boolean(rejectTarget)}
        onOpenChange={(open) => {
          if (!open) setRejectTarget(null)
        }}
        title={t('enrollment.rejectConfirm.title', { name: rejectTarget?.name ?? '' })}
        description={t('enrollment.rejectConfirm.description')}
        confirmLabel={t('enrollment.rejectConfirm.confirm')}
        variant="destructive"
        loading={rejecting}
        onConfirm={() => (rejectTarget ? handleReject(rejectTarget) : undefined)}
      />
    </LayerCard>
  )
}

CompanyEnrollmentPanel.displayName = 'CompanyEnrollmentPanel'
